import { useState, useEffect, useCallback } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
  PieChart, Pie, Cell, LineChart, Line,
} from "recharts";
import api from "../services/api";

// ── Helpers ───────────────────────────────────────────────────
const fmt  = (n) => new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);
const fmtK = (n) => Math.abs(n) >= 1000 ? `${(n / 1000).toFixed(0)}k` : String(Math.round(n || 0));
const currentMonth = () => new Date().toISOString().slice(0, 7);

function Pill({ color, label }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 9px", borderRadius: 20, background: `${color}22`, color, fontSize: 11, fontWeight: 700 }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, display: "inline-block" }} />
      {label}
    </span>
  );
}

function KpiCard({ label, value, sub, color = "#1d9e75", icon }) {
  return (
    <div style={{ background: "var(--card-bg)", border: "1px solid var(--border-color)", borderRadius: 12, padding: "16px 18px", flex: 1, minWidth: 160 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        {icon && <span style={{ fontSize: 18 }}>{icon}</span>}
        <span style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// ONGLET 1 : TABLEAU DE BORD
// ════════════════════════════════════════════════════════════════
function DashboardTab({ companyId }) {
  const [data, setData] = useState(null);
  const [evolution, setEvolution] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    if (!companyId) return;
    setLoading(true);
    Promise.all([
      api.analytics.dashboard(companyId),
      api.analytics.reportEvolution({ company_id: companyId, months: 6 }),
    ]).then(([d, ev]) => { setData(d); setEvolution(ev); }).catch(() => {}).finally(() => setLoading(false));
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Loader />;
  if (!data) return <Empty msg="Impossible de charger le tableau de bord." />;
  if (!data.codes.length) return <SetupBanner companyId={companyId} onDone={load} />;

  const { codes, totals, insights, unaffected_count } = data;

  // Données bar chart comparison M vs M-1
  const barData = codes.map(c => ({
    name: c.code,
    label: c.label,
    "Mois actuel": Math.round(c.revenue_ht),
    fill: c.color,
  }));

  // Données pie
  const pieData = codes.filter(c => c.revenue_ht > 0).map(c => ({ name: c.label, value: Math.round(c.revenue_ht), color: c.color }));

  // Données line (evolution)
  const lineData = (evolution?.periods || []).map((p, i) => {
    const pt = { period: p.slice(5) };
    (evolution?.series || []).forEach(s => { pt[s.label] = s.data[i]?.revenue || 0; });
    return pt;
  });

  return (
    <div>
      {/* Alertes IA */}
      {(insights.length > 0 || unaffected_count > 0) && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
          {unaffected_count > 0 && (
            <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: "#dc2626", display: "flex", alignItems: "center", gap: 8 }}>
              <span>⚠️</span>
              <span><strong>{unaffected_count} document(s)</strong> sans code analytique sur les 3 derniers mois.</span>
            </div>
          )}
          {insights.map((ins, i) => {
            const colors = { success: ["rgba(16,185,129,0.08)", "rgba(16,185,129,0.25)", "#065f46"], warning: ["rgba(245,158,11,0.08)", "rgba(245,158,11,0.25)", "#92400e"], danger: ["rgba(239,68,68,0.08)", "rgba(239,68,68,0.25)", "#dc2626"] };
            const [bg, bd, fg] = colors[ins.type] || colors.success;
            const icons = { success: "💡", warning: "⚡", danger: "🔴" };
            return (
              <div key={i} style={{ background: bg, border: `1px solid ${bd}`, borderRadius: 10, padding: "10px 14px", fontSize: 13, color: fg, display: "flex", gap: 8 }}>
                <span>{icons[ins.type]}</span> <span>{ins.text}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* KPIs */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
        {codes.map(c => (
          <KpiCard key={c.id}
            label={c.label} color={c.color} icon="📊"
            value={`${fmt(c.revenue_ht)} €`}
            sub={`${c.revenue_pct}% du CA · Marge ${c.margin_pct.toFixed(0)}%`}
          />
        ))}
        <KpiCard label="Total société" color="#1d9e75" icon="🏢"
          value={`${fmt(totals.revenue_ht)} €`}
          sub={`Charges ${fmt(totals.expenses_ht)} €`}
        />
      </div>

      {/* Graphiques ligne 1 */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        <div style={cardStyle}>
          <h4 style={chartTitle}>CA par activité — mois courant</h4>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={barData} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
              <XAxis dataKey="name" tick={{ fill: "var(--text-muted)", fontSize: 11 }} />
              <YAxis tickFormatter={fmtK} tick={{ fill: "var(--text-muted)", fontSize: 11 }} />
              <Tooltip formatter={(v) => [`${fmt(v)} €`, "CA HT"]} contentStyle={tooltipStyle} />
              {codes.map(c => <Bar key={c.code} dataKey="Mois actuel" name={c.label} fill={c.color} radius={[4, 4, 0, 0]} />)}
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div style={cardStyle}>
          <h4 style={chartTitle}>Répartition du CA</h4>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip formatter={(v) => [`${fmt(v)} €`]} contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Évolution 6 mois */}
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <h4 style={chartTitle}>Évolution CA sur 6 mois par activité</h4>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={lineData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
            <XAxis dataKey="period" tick={{ fill: "var(--text-muted)", fontSize: 11 }} />
            <YAxis tickFormatter={fmtK} tick={{ fill: "var(--text-muted)", fontSize: 11 }} />
            <Tooltip formatter={(v) => [`${fmt(v)} €`]} contentStyle={tooltipStyle} />
            <Legend />
            {(evolution?.series || []).map(s => (
              <Line key={s.id} type="monotone" dataKey={s.label} stroke={s.color} strokeWidth={2} dot={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Tableau synthèse */}
      <div style={cardStyle}>
        <h4 style={chartTitle}>Synthèse par activité — {data.period}</h4>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
              {["Activité", "CA HT", "Charges", "Marge", "% Marge", "% CA"].map(h => (
                <th key={h} style={{ padding: "8px 10px", textAlign: h === "Activité" ? "left" : "right", color: "var(--text-muted)", fontWeight: 600, fontSize: 11 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {codes.map(c => (
              <tr key={c.id} style={{ borderBottom: "0.5px solid var(--border-color)" }}>
                <td style={{ padding: "10px 10px" }}><Pill color={c.color} label={c.label} /></td>
                <td style={{ textAlign: "right", padding: "10px 10px", fontWeight: 600 }}>{fmt(c.revenue_ht)} €</td>
                <td style={{ textAlign: "right", padding: "10px 10px", color: "#ef4444" }}>{fmt(c.expenses_ht)} €</td>
                <td style={{ textAlign: "right", padding: "10px 10px", color: c.margin_ht >= 0 ? "#10b981" : "#ef4444", fontWeight: 600 }}>{fmt(c.margin_ht)} €</td>
                <td style={{ textAlign: "right", padding: "10px 10px" }}>{c.margin_pct.toFixed(1)}%</td>
                <td style={{ textAlign: "right", padding: "10px 10px", color: "var(--text-muted)" }}>{c.revenue_pct}%</td>
              </tr>
            ))}
            <tr style={{ borderTop: "2px solid var(--border-color)", fontWeight: 700 }}>
              <td style={{ padding: "10px 10px" }}>TOTAL</td>
              <td style={{ textAlign: "right", padding: "10px 10px" }}>{fmt(data.totals.revenue_ht)} €</td>
              <td style={{ textAlign: "right", padding: "10px 10px", color: "#ef4444" }}>{fmt(data.totals.expenses_ht)} €</td>
              <td style={{ textAlign: "right", padding: "10px 10px", color: data.totals.margin_ht >= 0 ? "#10b981" : "#ef4444" }}>{fmt(data.totals.margin_ht)} €</td>
              <td style={{ textAlign: "right", padding: "10px 10px" }}>{data.totals.margin_pct?.toFixed(1)}%</td>
              <td style={{ textAlign: "right", padding: "10px 10px" }}>100%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// ONGLET 2 : COMPTE DE RÉSULTAT ANALYTIQUE
// ════════════════════════════════════════════════════════════════
function PLTab({ companyId }) {
  const [axes, setAxes] = useState([]);
  const [selectedAxis, setSelectedAxis] = useState(null);
  const [period, setPeriod] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drillDown, setDrillDown] = useState(null);
  const [vsLastYear, setVsLastYear] = useState(false);
  const [dataLY, setDataLY] = useState(null);

  useEffect(() => {
    if (!companyId) return;
    api.analytics.axes(companyId).then(a => { setAxes(a); if (a.length) setSelectedAxis(a[0].id); }).catch(() => {});
  }, [companyId]);

  const load = useCallback(() => {
    if (!companyId) return;
    setLoading(true);
    const p1 = api.analytics.reportPL({ company_id: companyId, period, axis_id: selectedAxis || undefined });
    const p2 = vsLastYear ? api.analytics.reportPL({ company_id: companyId, period: period.replace(/^(\d{4})/, y => String(parseInt(y) - 1)), axis_id: selectedAxis || undefined }) : Promise.resolve(null);
    Promise.all([p1, p2]).then(([d, dly]) => { setData(d); setDataLY(dly); }).catch(() => {}).finally(() => setLoading(false));
  }, [companyId, period, selectedAxis, vsLastYear]);

  useEffect(() => { load(); }, [load]);

  const exportCSV = () => {
    if (!data) return;
    const rows = [["Activité", "CA HT", "Charges", "Marge", "% Marge"]];
    data.codes.forEach(c => rows.push([c.label, c.revenue_ht, c.expenses_ht, c.margin_ht, c.margin_pct + "%"]));
    rows.push(["TOTAL", data.totals.revenue_ht, data.totals.expenses_ht, data.totals.margin_ht, data.totals.margin_pct + "%"]);
    const csv = rows.map(r => r.join(";")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `pl_analytique_${period}.csv`; a.click();
  };

  const codes = data?.codes || [];
  const headers = codes.map(c => c);

  const lyCode = (codeId) => dataLY?.codes?.find(c => c.id === codeId);

  return (
    <div>
      {/* Contrôles */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 20, flexWrap: "wrap" }}>
        <div>
          <label style={labelSt}>Axe analytique</label>
          <select style={inputSt} value={selectedAxis || ""} onChange={e => setSelectedAxis(e.target.value || null)}>
            <option value="">Tous les axes</option>
            {axes.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
        <div>
          <label style={labelSt}>Période</label>
          <input type="month" style={inputSt} value={period} onChange={e => setPeriod(e.target.value)} />
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text-secondary)", cursor: "pointer", marginTop: 16 }}>
          <input type="checkbox" checked={vsLastYear} onChange={e => setVsLastYear(e.target.checked)} />
          Comparer N-1
        </label>
        <button onClick={exportCSV} style={{ ...btnSec, marginTop: 16, marginLeft: "auto" }}>📥 Export CSV</button>
      </div>

      {loading ? <Loader /> : !data ? null : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "var(--bg-secondary)" }}>
                <th style={{ ...thSt, textAlign: "left", width: 180 }}>Ligne P&L</th>
                {headers.map(c => (
                  <th key={c.id} style={thSt}>
                    <Pill color={c.color} label={c.label} />
                  </th>
                ))}
                <th style={thSt}>Total société</th>
              </tr>
            </thead>
            <tbody>
              {[
                { label: "Chiffre d'affaires HT", key: "revenue_ht", color: "#10b981", bold: true, total: data.totals.revenue_ht },
                { label: "Achats & charges variables", key: "expenses_ht", color: "#ef4444", neg: true, total: data.totals.expenses_ht },
                { label: "Marge brute", key: "margin_ht", color: "#3b82f6", bold: true, total: data.totals.margin_ht, separator: true },
                { label: "Taux de marge (%)", key: "margin_pct", color: "var(--text-secondary)", unit: "%", total: data.totals.margin_pct },
              ].map(row => (
                <tr key={row.key} style={{ borderBottom: `${row.separator ? "2px" : "0.5px"} solid var(--border-color)` }}>
                  <td style={{ padding: "10px 12px", fontWeight: row.bold ? 700 : 400, color: row.color }}>{row.label}</td>
                  {codes.map(c => {
                    const val = c[row.key];
                    const lyVal = vsLastYear ? lyCode(c.id)?.[row.key] : null;
                    return (
                      <td key={c.id} style={{ textAlign: "right", padding: "10px 12px", cursor: row.key === "revenue_ht" ? "pointer" : "default", fontWeight: row.bold ? 700 : 400, color: row.color }}
                        onClick={() => row.key === "revenue_ht" && setDrillDown({ code: c, entries: c.entries || [] })}>
                        {row.unit ? `${val?.toFixed(1)}${row.unit}` : `${fmt(val)} €`}
                        {lyVal !== null && lyVal !== undefined && (
                          <div style={{ fontSize: 10, color: "var(--text-muted)" }}>N-1: {row.unit ? `${lyVal?.toFixed(1)}${row.unit}` : `${fmt(lyVal)} €`}</div>
                        )}
                      </td>
                    );
                  })}
                  <td style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700, color: row.color }}>
                    {row.unit ? `${row.total?.toFixed(1)}${row.unit}` : `${fmt(row.total)} €`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Drill-down modal */}
      {drillDown && (
        <div style={modalOverlay} onClick={() => setDrillDown(null)}>
          <div style={{ ...modalBox, maxWidth: 600 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h3 style={{ margin: 0, fontSize: 15 }}>Détail — <Pill color={drillDown.code.color} label={drillDown.code.label} /></h3>
              <span onClick={() => setDrillDown(null)} style={{ cursor: "pointer", fontSize: 18, color: "var(--text-muted)" }}>×</span>
            </div>
            {drillDown.entries.length === 0 ? (
              <p style={{ color: "var(--text-muted)", fontSize: 13 }}>Aucune écriture détaillée disponible.</p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead><tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                  {["Type", "Réf", "Montant HT", "% affecté"].map(h => <th key={h} style={{ padding: "6px 8px", textAlign: h.includes("Montant") || h.includes("%") ? "right" : "left", color: "var(--text-muted)" }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {drillDown.entries.map(e => (
                    <tr key={e.id} style={{ borderBottom: "0.5px solid var(--border-color)" }}>
                      <td style={{ padding: "7px 8px" }}><span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 10, background: "var(--bg-secondary)", color: "var(--text-muted)" }}>{e.document_type}</span></td>
                      <td style={{ padding: "7px 8px" }}>#{e.document_id}</td>
                      <td style={{ textAlign: "right", padding: "7px 8px", fontWeight: 600 }}>{fmt(e.amount_ht)} €</td>
                      <td style={{ textAlign: "right", padding: "7px 8px", color: "var(--text-muted)" }}>{e.percentage}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// ONGLET 3 : AFFECTATION ANALYTIQUE
// ════════════════════════════════════════════════════════════════
function AffectationTab({ companyId }) {
  const [unaffected, setUnaffected] = useState({ invoices: [], expenses: [] });
  const [codes, setCodes] = useState([]);
  const [rules, setRules] = useState([]);
  const [assignModal, setAssignModal] = useState(null);
  const [split, setSplit] = useState([]);
  const [splitMode, setSplitMode] = useState(false);
  const [newRule, setNewRule] = useState({ rule_type: "client", rule_value: "", analytics_code_id: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!companyId) return;
    Promise.all([
      api.analytics.unaffected(companyId),
      api.analytics.codes({ company_id: companyId }),
      api.analytics.rules(companyId),
    ]).then(([u, c, r]) => { setUnaffected(u); setCodes(c); setRules(r); }).catch(() => {});
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const openAssign = (doc) => {
    setAssignModal(doc);
    setSplitMode(false);
    setSplit([{ code_id: codes[0]?.id || "", pct: 100 }]);
  };

  const handleAssign = async (codeId) => {
    if (!assignModal) return;
    setSaving(true);
    try {
      await api.analytics.assign({ document_type: assignModal.type, document_id: assignModal.id, analytics_code_id: codeId, company_id: companyId });
      setAssignModal(null);
      load();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const handleSplit = async () => {
    if (!assignModal) return;
    const total = split.reduce((s, x) => s + parseFloat(x.pct || 0), 0);
    if (Math.abs(total - 100) > 0.1) { alert("Le total des pourcentages doit être 100%"); return; }
    setSaving(true);
    try {
      await api.analytics.assign({ document_type: assignModal.type, document_id: assignModal.id, company_id: companyId, split: split.map(s => ({ code_id: s.code_id, pct: parseFloat(s.pct) })) });
      setAssignModal(null); load();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const handleAddRule = async () => {
    if (!newRule.rule_value || !newRule.analytics_code_id) { alert("Renseignez tous les champs"); return; }
    await api.analytics.createRule({ ...newRule, company_id: companyId });
    setNewRule({ rule_type: "client", rule_value: "", analytics_code_id: "" });
    load();
  };

  const handleDeleteRule = async (id) => {
    await api.analytics.deleteRule(id);
    load();
  };

  const allDocs = [...(unaffected.invoices || []).map(d => ({ ...d, type: "facture" })), ...(unaffected.expenses || []).map(d => ({ ...d, type: "note_frais" }))];
  const totalPct = split.reduce((s, x) => s + parseFloat(x.pct || 0), 0);

  return (
    <div>
      {/* Documents non affectés */}
      <div style={cardStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <h4 style={{ margin: 0, fontSize: 14 }}>Documents sans code analytique</h4>
          {allDocs.length > 0 && (
            <span style={{ background: "rgba(239,68,68,0.12)", color: "#dc2626", padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{allDocs.length}</span>
          )}
        </div>
        {allDocs.length === 0 ? (
          <p style={{ color: "var(--text-muted)", fontSize: 13 }}>Tous les documents sont affectés.</p>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead><tr style={{ borderBottom: "1px solid var(--border-color)" }}>
              {["Type", "Réf / Libellé", "Date", "Montant HT", "Action"].map(h => <th key={h} style={{ ...thSt, textAlign: h === "Action" ? "center" : "left" }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {allDocs.map(doc => (
                <tr key={`${doc.type}-${doc.id}`} style={{ borderBottom: "0.5px solid var(--border-color)" }}>
                  <td style={{ padding: "8px 10px" }}>
                    <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 10, background: doc.type === "facture" ? "rgba(59,130,246,0.12)" : "rgba(245,158,11,0.12)", color: doc.type === "facture" ? "#3b82f6" : "#d97706" }}>{doc.type}</span>
                  </td>
                  <td style={{ padding: "8px 10px", fontWeight: 500 }}>{doc.ref} — {doc.label}</td>
                  <td style={{ padding: "8px 10px", color: "var(--text-muted)" }}>{doc.date}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 600 }}>{fmt(doc.amount_ht)} €</td>
                  <td style={{ padding: "8px 10px", textAlign: "center" }}>
                    <button onClick={() => openAssign(doc)} style={{ ...btnSec, fontSize: 11 }}>🏷️ Affecter</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Règles automatiques */}
      <div style={{ ...cardStyle, marginTop: 16 }}>
        <h4 style={{ margin: "0 0 14px", fontSize: 14 }}>Règles d'affectation automatique</h4>
        <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
          <select style={{ ...inputSt, flex: "0 0 140px" }} value={newRule.rule_type} onChange={e => setNewRule(r => ({ ...r, rule_type: e.target.value }))}>
            <option value="client">Client</option>
            <option value="fournisseur">Fournisseur</option>
            <option value="keyword">Mot-clé</option>
            <option value="country">Pays</option>
          </select>
          <input style={{ ...inputSt, flex: 1, minWidth: 140 }} placeholder={newRule.rule_type === "client" ? "Ex: Acme Corp" : newRule.rule_type === "country" ? "Ex: DE, ES..." : "Ex: Amazon"} value={newRule.rule_value} onChange={e => setNewRule(r => ({ ...r, rule_value: e.target.value }))} />
          <span style={{ alignSelf: "center", color: "var(--text-muted)", fontSize: 13 }}>→</span>
          <select style={{ ...inputSt, flex: "0 0 180px" }} value={newRule.analytics_code_id} onChange={e => setNewRule(r => ({ ...r, analytics_code_id: e.target.value }))}>
            <option value="">Code analytique</option>
            {codes.map(c => <option key={c.id} value={c.id}>{c.code} — {c.label}</option>)}
          </select>
          <button onClick={handleAddRule} style={btnPri}>+ Ajouter règle</button>
        </div>
        {rules.length === 0 ? <p style={{ color: "var(--text-muted)", fontSize: 13 }}>Aucune règle définie.</p> : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead><tr style={{ borderBottom: "1px solid var(--border-color)" }}>
              {["Type", "Valeur", "→ Code", ""].map(h => <th key={h} style={{ ...thSt, textAlign: "left" }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {rules.map(r => (
                <tr key={r.id} style={{ borderBottom: "0.5px solid var(--border-color)" }}>
                  <td style={{ padding: "8px 10px" }}><span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 10, background: "var(--bg-secondary)", color: "var(--text-muted)" }}>{r.rule_type}</span></td>
                  <td style={{ padding: "8px 10px", fontWeight: 500 }}>{r.rule_value}</td>
                  <td style={{ padding: "8px 10px" }}><Pill color={r.color} label={r.label} /></td>
                  <td style={{ padding: "8px 10px", textAlign: "right" }}><button onClick={() => handleDeleteRule(r.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444", fontSize: 14 }}>🗑</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Assign modal */}
      {assignModal && (
        <div style={modalOverlay} onClick={() => setAssignModal(null)}>
          <div style={{ ...modalBox, maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h3 style={{ margin: 0, fontSize: 15 }}>Affecter — {assignModal.ref}</h3>
              <span onClick={() => setAssignModal(null)} style={{ cursor: "pointer", fontSize: 18, color: "var(--text-muted)" }}>×</span>
            </div>
            <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 14 }}>Montant HT : <strong>{fmt(assignModal.amount_ht)} €</strong></p>

            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", marginBottom: 14 }}>
              <input type="checkbox" checked={splitMode} onChange={e => setSplitMode(e.target.checked)} />
              Répartir sur plusieurs activités
            </label>

            {!splitMode ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {codes.filter(c => c.is_active).map(c => (
                  <button key={c.id} onClick={() => handleAssign(c.id)} disabled={saving}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 9, border: `1px solid ${c.color}40`, background: `${c.color}12`, cursor: "pointer", textAlign: "left" }}>
                    <div style={{ width: 10, height: 10, borderRadius: "50%", background: c.color, flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>{c.label}</div>
                      <div style={{ fontSize: 10, color: "var(--text-muted)" }}>{c.code} — 100% ({fmt(assignModal.amount_ht)} €)</div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div>
                {split.map((s, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
                    <select style={{ ...inputSt, flex: 1 }} value={s.code_id} onChange={e => setSplit(sp => sp.map((x, j) => j === i ? { ...x, code_id: e.target.value } : x))}>
                      {codes.filter(c => c.is_active).map(c => <option key={c.id} value={c.id}>{c.code} — {c.label}</option>)}
                    </select>
                    <input type="number" min="0" max="100" style={{ ...inputSt, width: 70 }} value={s.pct} onChange={e => setSplit(sp => sp.map((x, j) => j === i ? { ...x, pct: e.target.value } : x))} />
                    <span style={{ fontSize: 12, color: "var(--text-muted)" }}>%</span>
                    {split.length > 1 && <button onClick={() => setSplit(sp => sp.filter((_, j) => j !== i))} style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444" }}>×</button>}
                  </div>
                ))}
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
                  <button onClick={() => setSplit(sp => [...sp, { code_id: codes[0]?.id || "", pct: 0 }])} style={btnSec}>+ Ajouter</button>
                  <span style={{ fontSize: 12, color: Math.abs(totalPct - 100) < 0.1 ? "#10b981" : "#ef4444", fontWeight: 600 }}>Total : {totalPct}%</span>
                  <button onClick={handleSplit} disabled={saving || Math.abs(totalPct - 100) > 0.1} style={{ ...btnPri, marginLeft: "auto" }}>Valider la répartition</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// ONGLET 4 : BUDGET VS RÉALISÉ
// ════════════════════════════════════════════════════════════════
function BudgetTab({ companyId }) {
  const [period, setPeriod] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [codes, setCodes] = useState([]);
  const [budgetForm, setBudgetForm] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!companyId) return;
    Promise.all([
      api.analytics.reportBudget({ company_id: companyId, period }),
      api.analytics.codes({ company_id: companyId }),
      api.analytics.budgets({ company_id: companyId, period }),
    ]).then(([d, c, b]) => {
      setData(d);
      setCodes(c);
      const form = {};
      c.forEach(code => {
        const bgt = b.find(x => x.analytics_code_id === code.id);
        form[code.id] = bgt?.budget_revenue_ht || code.budget_monthly || 0;
      });
      setBudgetForm(form);
    }).catch(() => {});
  }, [companyId, period]);

  useEffect(() => { load(); }, [load]);

  const handleSaveBudgets = async () => {
    setSaving(true);
    try {
      const items = codes.map(c => ({
        company_id: companyId, analytics_code_id: c.id, period,
        budget_revenue_ht: parseFloat(budgetForm[c.id] || 0),
        budget_expenses_ht: 0, budget_margin: 0,
      }));
      await api.analytics.saveBudgets(items);
      setEditMode(false);
      load();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const rows = data?.codes || [];

  return (
    <div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 20, flexWrap: "wrap" }}>
        <div>
          <label style={labelSt}>Période</label>
          <input type="month" style={inputSt} value={period} onChange={e => setPeriod(e.target.value)} />
        </div>
        <button onClick={() => setEditMode(e => !e)} style={{ ...btnSec, marginTop: 16 }}>
          {editMode ? "Annuler" : "✏️ Saisir objectifs"}
        </button>
        {editMode && <button onClick={handleSaveBudgets} disabled={saving} style={{ ...btnPri, marginTop: 16 }}>{saving ? "Enregistrement…" : "💾 Enregistrer"}</button>}
      </div>

      {editMode && (
        <div style={{ ...cardStyle, marginBottom: 16, display: "flex", flexWrap: "wrap", gap: 14 }}>
          {codes.map(c => (
            <div key={c.id} style={{ display: "flex", flex: "0 0 200px", flexDirection: "column", gap: 4 }}>
              <label style={{ ...labelSt, color: c.color }}>{c.label} ({c.code})</label>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <input type="number" min="0" step="100" value={budgetForm[c.id] || ""} onChange={e => setBudgetForm(f => ({ ...f, [c.id]: e.target.value }))} style={{ ...inputSt, flex: 1 }} placeholder="0" />
                <span style={{ fontSize: 12, color: "var(--text-muted)" }}>€</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {rows.length === 0 ? (
        <Empty msg="Aucune activité configurée." />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {rows.map(r => (
            <div key={r.id} style={{ ...cardStyle, borderLeft: `4px solid ${r.color}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <Pill color={r.color} label={r.label} />
                    {r.on_track === true && <span style={{ fontSize: 11, background: "rgba(16,185,129,0.12)", color: "#10b981", padding: "2px 8px", borderRadius: 10 }}>✓ En bonne voie</span>}
                    {r.on_track === false && <span style={{ fontSize: 11, background: "rgba(239,68,68,0.12)", color: "#dc2626", padding: "2px 8px", borderRadius: 10 }}>⚠ Retard</span>}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                    Réalisé : <strong style={{ color: "var(--text-primary)" }}>{fmt(r.actual_revenue)} €</strong>
                    {" · "} Objectif : <strong>{fmt(r.budget_revenue)} €</strong>
                    {r.gap !== 0 && <span style={{ color: r.gap >= 0 ? "#10b981" : "#ef4444", marginLeft: 8, fontWeight: 600 }}>{r.gap >= 0 ? "+" : ""}{fmt(r.gap)} € ({r.gap_pct !== null ? `${r.gap_pct >= 0 ? "+" : ""}${r.gap_pct?.toFixed(1)}%` : "—"})</span>}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: r.color }}>{r.progress}%</div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)" }}>Projection : {fmt(r.projection)} €</div>
                </div>
              </div>
              {/* Barre de progression */}
              <div style={{ height: 10, background: "var(--bg-secondary)", borderRadius: 5, overflow: "hidden", marginTop: 12 }}>
                <div style={{ height: "100%", width: `${r.progress}%`, background: r.on_track === false ? "#ef4444" : r.color, borderRadius: 5, transition: "width 0.5s" }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// ONGLET 5 : CONFIGURATION DES AXES
// ════════════════════════════════════════════════════════════════
function ConfigTab({ companyId }) {
  const [axes, setAxes]     = useState([]);
  const [codes, setCodes]   = useState({});
  const [editAxis, setEditAxis]  = useState(null);
  const [newCode, setNewCode]    = useState({});
  const [editCode, setEditCode]  = useState(null);
  const [newAxis, setNewAxis]    = useState({ name: "", description: "", is_required: false });
  const [showNewAxis, setShowNewAxis] = useState(false);
  const [showInitModal, setShowInitModal] = useState(false);
  const [template, setTemplate] = useState("ecommerce");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!companyId) return;
    api.analytics.axes(companyId).then(a => {
      setAxes(a);
      a.forEach(ax => {
        api.analytics.codes({ axis_id: ax.id, company_id: companyId }).then(c => setCodes(prev => ({ ...prev, [ax.id]: c })));
      });
    }).catch(() => {});
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const handleCreateAxis = async () => {
    if (!newAxis.name) return;
    setSaving(true);
    await api.analytics.createAxis({ ...newAxis, company_id: companyId });
    setNewAxis({ name: "", description: "", is_required: false });
    setShowNewAxis(false);
    setSaving(false);
    load();
  };

  const handleToggleAxis = async (ax) => {
    await api.analytics.updateAxis(ax.id, { is_active: ax.is_active ? 0 : 1 });
    load();
  };

  const handleDeleteAxis = async (ax) => {
    if (!window.confirm(`Supprimer l'axe "${ax.name}" ?`)) return;
    try { await api.analytics.deleteAxis(ax.id); load(); }
    catch (e) { alert(e.message); }
  };

  const handleCreateCode = async (axisId) => {
    const nc = newCode[axisId] || {};
    if (!nc.code || !nc.label) return alert("Code et libellé requis");
    await api.analytics.createCode({ axis_id: axisId, company_id: companyId, code: nc.code, label: nc.label, color: nc.color || "#3b82f6" });
    setNewCode(p => ({ ...p, [axisId]: { code: "", label: "", color: "#3b82f6" } }));
    load();
  };

  const handleUpdateCode = async () => {
    if (!editCode) return;
    await api.analytics.updateCode(editCode.id, { code: editCode.code, label: editCode.label, color: editCode.color, is_active: editCode.is_active });
    setEditCode(null);
    load();
  };

  const handleDeleteCode = async (id) => {
    try { await api.analytics.deleteCode(id); load(); }
    catch (e) { alert(e.message); }
  };

  const handleInit = async () => {
    try {
      await api.analytics.init({ company_id: companyId, template });
      setShowInitModal(false);
      load();
    } catch (e) { alert(e.message); }
  };

  const COLORS_PRESET = ["#10b981", "#3b82f6", "#f59e0b", "#8b5cf6", "#ef4444", "#06b6d4", "#f97316", "#84cc16"];

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
        {axes.length === 0 && (
          <button onClick={() => setShowInitModal(true)} style={btnPri}>⚡ Initialiser avec un modèle</button>
        )}
        <button onClick={() => setShowNewAxis(s => !s)} style={btnSec}>+ Nouvel axe analytique</button>
      </div>

      {showNewAxis && (
        <div style={{ ...cardStyle, marginBottom: 16, border: "1px dashed #1d9e75" }}>
          <h4 style={{ margin: "0 0 12px", fontSize: 14, color: "#1d9e75" }}>Nouvel axe analytique</h4>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label style={labelSt}>Nom *</label>
              <input style={inputSt} value={newAxis.name} onChange={e => setNewAxis(a => ({ ...a, name: e.target.value }))} placeholder="Ex: Type d'activité" />
            </div>
            <div style={{ flex: 2, minWidth: 200 }}>
              <label style={labelSt}>Description</label>
              <input style={inputSt} value={newAxis.description} onChange={e => setNewAxis(a => ({ ...a, description: e.target.value }))} placeholder="Ex: Segmentation par canal de vente" />
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer", paddingTop: 18 }}>
              <input type="checkbox" checked={newAxis.is_required} onChange={e => setNewAxis(a => ({ ...a, is_required: e.target.checked }))} />
              Axe obligatoire
            </label>
            <button onClick={handleCreateAxis} disabled={saving} style={{ ...btnPri, alignSelf: "flex-end" }}>Créer</button>
          </div>
        </div>
      )}

      {axes.length === 0 && !showNewAxis && (
        <Empty msg="Aucun axe analytique configuré pour cette société." />
      )}

      {axes.map(ax => (
        <div key={ax.id} style={{ ...cardStyle, marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <h4 style={{ margin: 0, fontSize: 15, color: "var(--text-primary)" }}>{ax.name}</h4>
                {ax.is_required ? <span style={{ fontSize: 10, background: "rgba(239,68,68,0.12)", color: "#dc2626", padding: "2px 7px", borderRadius: 10 }}>OBLIGATOIRE</span> : null}
                {!ax.is_active && <span style={{ fontSize: 10, background: "var(--bg-secondary)", color: "var(--text-muted)", padding: "2px 7px", borderRadius: 10 }}>INACTIF</span>}
              </div>
              {ax.description && <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{ax.description}</div>}
            </div>
            <button onClick={() => handleToggleAxis(ax)} style={{ ...btnSec, fontSize: 11 }}>{ax.is_active ? "Désactiver" : "Activer"}</button>
            <button onClick={() => handleDeleteAxis(ax)} style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444", fontSize: 16 }} title="Supprimer l'axe">🗑</button>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {(codes[ax.id] || []).map(c => (
              <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "var(--bg-secondary)", borderRadius: 8, border: `1px solid ${c.color}30` }}>
                {editCode?.id === c.id ? (
                  <>
                    <input value={editCode.color} type="color" onChange={e => setEditCode(x => ({ ...x, color: e.target.value }))} style={{ width: 30, height: 30, border: "none", borderRadius: 6, cursor: "pointer", padding: 0 }} />
                    <input value={editCode.code} onChange={e => setEditCode(x => ({ ...x, code: e.target.value }))} style={{ ...inputSt, width: 80 }} placeholder="CODE" />
                    <input value={editCode.label} onChange={e => setEditCode(x => ({ ...x, label: e.target.value }))} style={{ ...inputSt, flex: 1 }} />
                    <button onClick={handleUpdateCode} style={btnPri}>OK</button>
                    <button onClick={() => setEditCode(null)} style={btnSec}>✕</button>
                  </>
                ) : (
                  <>
                    <div style={{ width: 14, height: 14, borderRadius: "50%", background: c.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: c.color, width: 60 }}>{c.code}</span>
                    <span style={{ fontSize: 13, color: "var(--text-primary)", flex: 1 }}>{c.label}</span>
                    {!c.is_active && <span style={{ fontSize: 10, color: "var(--text-muted)" }}>inactif</span>}
                    <button onClick={() => setEditCode({ ...c })} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", fontSize: 14 }}>✏️</button>
                    <button onClick={() => handleDeleteCode(c.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444", fontSize: 14 }}>🗑</button>
                  </>
                )}
              </div>
            ))}

            {/* Nouveau code */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0" }}>
              <input type="color" value={newCode[ax.id]?.color || "#3b82f6"}
                onChange={e => setNewCode(p => ({ ...p, [ax.id]: { ...(p[ax.id] || {}), color: e.target.value } }))}
                style={{ width: 30, height: 30, border: "none", borderRadius: 6, cursor: "pointer", padding: 0, background: "none" }} />
              <div style={{ display: "flex", gap: 4 }}>
                {COLORS_PRESET.map(col => (
                  <div key={col} onClick={() => setNewCode(p => ({ ...p, [ax.id]: { ...(p[ax.id] || {}), color: col } }))}
                    style={{ width: 16, height: 16, borderRadius: "50%", background: col, cursor: "pointer", border: newCode[ax.id]?.color === col ? "2px solid white" : "none" }} />
                ))}
              </div>
              <input value={newCode[ax.id]?.code || ""} onChange={e => setNewCode(p => ({ ...p, [ax.id]: { ...(p[ax.id] || {}), code: e.target.value } }))}
                style={{ ...inputSt, width: 80 }} placeholder="CODE" />
              <input value={newCode[ax.id]?.label || ""} onChange={e => setNewCode(p => ({ ...p, [ax.id]: { ...(p[ax.id] || {}), label: e.target.value } }))}
                style={{ ...inputSt, flex: 1 }} placeholder="Ex: E-commerce BtoC" />
              <button onClick={() => handleCreateCode(ax.id)} style={btnPri}>+ Ajouter</button>
            </div>
          </div>
        </div>
      ))}

      {/* Modal init avec template */}
      {showInitModal && (
        <div style={modalOverlay} onClick={() => setShowInitModal(false)}>
          <div style={{ ...modalBox, maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 16px", fontSize: 16 }}>Initialiser la comptabilité analytique</h3>
            <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 16 }}>Choisissez un modèle prédéfini pour démarrer rapidement. Vous pourrez personnaliser les axes et codes ensuite.</p>
            {[
              { id: "ecommerce", label: "E-commerce", desc: "BtoC / BtoB / Dropshipping / Marketplace", icon: "🛒" },
              { id: "international", label: "Commerce international", desc: "France / Europe / Export hors UE / DOM-TOM", icon: "🌍" },
              { id: "agence", label: "Agence / Services", desc: "Forfait / Régie / Formation / Maintenance", icon: "💼" },
            ].map(t => (
              <div key={t.id} onClick={() => setTemplate(t.id)}
                style={{ display: "flex", gap: 12, padding: "12px 14px", borderRadius: 10, border: `2px solid ${template === t.id ? "#1d9e75" : "var(--border-color)"}`, background: template === t.id ? "rgba(29,158,117,0.08)" : "var(--bg-secondary)", cursor: "pointer", marginBottom: 10 }}>
                <span style={{ fontSize: 24 }}>{t.icon}</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: "var(--text-primary)" }}>{t.label}</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{t.desc}</div>
                </div>
              </div>
            ))}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
              <button onClick={() => setShowInitModal(false)} style={btnSec}>Annuler</button>
              <button onClick={handleInit} style={btnPri}>Initialiser avec ce modèle</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// COMPOSANTS UTILITAIRES
// ════════════════════════════════════════════════════════════════
function SetupBanner({ companyId, onDone }) {
  const [template, setTemplate] = useState("ecommerce");
  const [loading, setLoading] = useState(false);
  const handleInit = async () => {
    setLoading(true);
    try {
      await api.analytics.init({ company_id: companyId, template });
      onDone();
    } catch (e) { alert(e.message); }
    finally { setLoading(false); }
  };
  return (
    <div style={{ textAlign: "center", padding: 60, background: "var(--card-bg)", borderRadius: 14, border: "1px dashed var(--border-color)" }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>📊</div>
      <h3 style={{ margin: "0 0 8px" }}>Comptabilité analytique non configurée</h3>
      <p style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 20 }}>Définissez vos axes analytiques pour commencer à analyser vos performances par activité.</p>
      <div style={{ display: "flex", gap: 10, justifyContent: "center", marginBottom: 20, flexWrap: "wrap" }}>
        {[{ id: "ecommerce", label: "E-commerce", icon: "🛒" }, { id: "international", label: "International", icon: "🌍" }, { id: "agence", label: "Services", icon: "💼" }].map(t => (
          <button key={t.id} onClick={() => setTemplate(t.id)}
            style={{ padding: "10px 18px", borderRadius: 10, border: `2px solid ${template === t.id ? "#1d9e75" : "var(--border-color)"}`, background: template === t.id ? "rgba(29,158,117,0.1)" : "var(--bg-secondary)", cursor: "pointer", fontSize: 13, color: "var(--text-primary)" }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>
      <button onClick={handleInit} disabled={loading} style={{ ...btnPri, padding: "12px 28px", fontSize: 14 }}>
        {loading ? "Initialisation…" : "⚡ Démarrer avec ce modèle"}
      </button>
      <p style={{ color: "var(--text-muted)", fontSize: 11, marginTop: 12 }}>Ou allez dans l'onglet "Configuration" pour créer vos axes manuellement.</p>
    </div>
  );
}

function Loader() {
  return <div style={{ textAlign: "center", padding: 40, color: "var(--text-muted)", fontSize: 13 }}>Chargement…</div>;
}
function Empty({ msg }) {
  return <div style={{ textAlign: "center", padding: 40, color: "var(--text-muted)", fontSize: 13, background: "var(--card-bg)", borderRadius: 12, border: "1px dashed var(--border-color)" }}>{msg}</div>;
}

// ════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ════════════════════════════════════════════════════════════════
const TABS = [
  { id: "dashboard",    label: "📊 Tableau de bord" },
  { id: "pl",          label: "📋 Compte de résultat" },
  { id: "affectation", label: "🏷️ Affectation" },
  { id: "budget",      label: "🎯 Budget vs Réalisé" },
  { id: "config",      label: "⚙️ Configuration" },
];

export default function ComptaAnalytique({ currentCompanyId }) {
  const [tab, setTab] = useState("dashboard");
  const [showHelp, setShowHelp] = useState(false);

  if (!currentCompanyId) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>
        Sélectionnez une société dans la barre latérale.
      </div>
    );
  }

  const tabProps = { companyId: currentCompanyId };

  return (
    <div style={{ padding: "20px 24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 700, color: "var(--text-primary)" }}>
            📊 Comptabilité Analytique
          </h2>
          <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>
            Analysez vos performances financières par activité · axe · zone géographique
          </p>
        </div>
        <button
          onClick={() => setShowHelp(true)}
          style={{ ...btnSec, display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}
        >
          ❓ Comment ça marche
        </button>
      </div>

      {/* Modal aide */}
      {showHelp && (
        <div style={modalOverlay} onClick={e => e.target === e.currentTarget && setShowHelp(false)}>
          <div style={{ background: "var(--card-bg)", borderRadius: 14, padding: 28, maxWidth: 620, width: "100%", maxHeight: "85vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
              <span style={{ fontSize: 20, marginRight: 10 }}>📊</span>
              <span style={{ fontWeight: 700, fontSize: 17, flex: 1 }}>Comment fonctionne la comptabilité analytique ?</span>
              <button onClick={() => setShowHelp(false)} style={{ ...btnSec, padding: "4px 10px", fontSize: 16 }}>✕</button>
            </div>

            <p style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.7, marginBottom: 16 }}>
              La comptabilité analytique vous permet de répartir vos revenus et dépenses selon des <strong>axes d'analyse</strong> personnalisés, au-delà de la comptabilité générale. Chaque axe est découpé en <strong>codes analytiques</strong>.
            </p>

            <div style={{ background: "var(--bg-secondary)", borderRadius: 10, padding: "14px 16px", marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: "var(--text-primary)" }}>🔧 Principe de fonctionnement</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {[
                  ["1. Créer des axes", "Rendez-vous dans l'onglet Configuration → Axes. Ex : « Type d'activité », « Zone commerciale »."],
                  ["2. Créer des codes", "Sous chaque axe, définissez vos codes. Ex : BtoC, BtoB, Dropshipping sous « Type d'activité »."],
                  ["3. Affecter les transactions", "Dans l'onglet Affectation, associez chaque facture ou dépense à un code analytique."],
                  ["4. Analyser", "Les onglets Tableau de bord et P&L affichent vos résultats ventilés par axe."],
                ].map(([title, desc]) => (
                  <div key={title} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <span style={{ fontWeight: 700, fontSize: 12, color: "#1d9e75", whiteSpace: "nowrap", minWidth: 140 }}>{title}</span>
                    <span style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5 }}>{desc}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: "var(--text-primary)" }}>💡 Exemples concrets</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              <div style={{ background: "#1d9e7511", border: "1px solid #1d9e7530", borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ fontWeight: 700, fontSize: 12, color: "#1d9e75", marginBottom: 8 }}>🏪 PLACE PRIVEE SAS</div>
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 6 }}>Axe : <strong>Type d'activité</strong></div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {[
                    ["BtoC",         "Ventes directes aux particuliers"],
                    ["BtoB",         "Ventes aux professionnels"],
                    ["Dropshipping", "Ventes via fournisseurs tiers"],
                  ].map(([code, desc]) => (
                    <div key={code} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 5, background: "#1d9e7522", color: "#1d9e75", fontWeight: 700 }}>{code}</span>
                      <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{desc}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ background: "#185fa511", border: "1px solid #185fa530", borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ fontWeight: 700, fontSize: 12, color: "#185fa5", marginBottom: 8 }}>🏗️ TFBC</div>
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 6 }}>Axe : <strong>Zone commerciale</strong></div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {[
                    ["France",        "Marchés domestiques"],
                    ["Export",        "Union Européenne"],
                    ["International", "Hors UE / monde"],
                  ].map(([code, desc]) => (
                    <div key={code} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 5, background: "#185fa522", color: "#185fa5", fontWeight: 700 }}>{code}</span>
                      <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#92400e" }}>
              💡 <strong>Conseil :</strong> Commencez par 1 à 2 axes maximum. Une structure trop complexe complique l'affectation. Vous pouvez toujours ajouter des axes et codes plus tard.
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
              <button onClick={() => { setShowHelp(false); setTab("config"); }} style={{ ...btnSec, marginRight: 8 }}>
                ⚙️ Aller à la configuration
              </button>
              <button onClick={() => setShowHelp(false)} style={btnPri}>
                Compris !
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 2, marginBottom: 24, borderBottom: "1px solid var(--border-color)" }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: "9px 16px", border: "none", borderRadius: "8px 8px 0 0", cursor: "pointer",
            fontSize: 13, fontWeight: 500, background: tab === t.id ? "var(--card-bg)" : "transparent",
            color: tab === t.id ? "var(--text-primary)" : "var(--text-muted)",
            borderBottom: tab === t.id ? "2px solid #1d9e75" : "2px solid transparent",
            transition: "all 0.12s",
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "dashboard"    && <DashboardTab    {...tabProps} />}
      {tab === "pl"           && <PLTab           {...tabProps} />}
      {tab === "affectation"  && <AffectationTab  {...tabProps} />}
      {tab === "budget"       && <BudgetTab        {...tabProps} />}
      {tab === "config"       && <ConfigTab        {...tabProps} />}
    </div>
  );
}

// ── Styles partagés ────────────────────────────────────────────
const cardStyle = { background: "var(--card-bg)", border: "1px solid var(--border-color)", borderRadius: 12, padding: "18px 20px" };
const chartTitle = { margin: "0 0 14px", fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" };
const tooltipStyle = { background: "var(--card-bg)", border: "1px solid var(--border-color)", borderRadius: 8, fontSize: 12 };
const thSt = { padding: "9px 10px", fontWeight: 600, fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", textAlign: "right", letterSpacing: "0.05em" };
const labelSt = { display: "block", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" };
const inputSt = { padding: "7px 10px", borderRadius: 7, border: "1px solid var(--border-color)", background: "var(--bg-secondary)", color: "var(--text-primary)", fontSize: 13, outline: "none" };
const btnPri = { padding: "8px 18px", borderRadius: 8, border: "none", background: "var(--g)", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" };
const btnSec = { padding: "7px 14px", borderRadius: 8, border: "1px solid var(--border-color)", background: "var(--bg-secondary)", color: "var(--text-primary)", fontSize: 13, cursor: "pointer" };
const modalOverlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 };
const modalBox = { background: "var(--card-bg)", border: "1px solid var(--border-color)", borderRadius: 14, padding: 24, width: "100%", maxHeight: "90vh", overflowY: "auto" };
